import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { AlertFlashService, UiBtnDirective } from '@callrail/looky/ui';
import { MigrationService } from '../../services/migration/migration.service';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import {
  email,
  form,
  FormField,
  minLength,
  required,
} from '@angular/forms/signals';
import { CodeInputComponent } from '../code-input/code-input.component';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'anbs-email-first-login',
  imports: [FormField, UiBtnDirective, CodeInputComponent, FormsModule],
  templateUrl: './email-first-login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailFirstLoginComponent {
  private alertFlash = inject(AlertFlashService);
  private migrationService = inject(MigrationService);
  private router = inject(Router);
  credentials = model({
    email: '',
    password: '',
  });
  credentialsForm = form(this.credentials, (creds) => {
    required(creds.email);
    email(creds.email);
    required(creds.password);
    minLength(creds.password, 6);
  });
  mfaFormModel = model({
    code: '',
  });
  mfaForm = form(this.mfaFormModel, (mfa) => {
    required(mfa.code, { message: 'is required' });
    minLength(mfa.code, 6, { message: 'must be 6 digits' });
  });
  otpContinue = viewChild<ElementRef>('otpContinue');
  formState = signal<'enterEmail' | 'login' | 'mfa'>('enterEmail');
  showPassword = signal(false);
  checkingEmail = signal(false);

  private passwordEl = viewChild<ElementRef>('password');
  private emailEl = viewChild<ElementRef>('email');

  constructor() {
    effect(() => {
      if (this.mfaFormModel().code.length === 6) {
        this.otpContinue()?.nativeElement.focus();
      }
    });
  }

  continue() {
    switch (this.formState()) {
      case 'enterEmail':
        if (this.credentialsForm.email().invalid()) return;
        this.checkingEmail.set(true);
        this.migrationService
          .checkEmail$(this.credentialsForm.email().value())
          .pipe(finalize(() => this.checkingEmail.set(false)))
          .subscribe({
            next: (resp) => {
              if (!resp.migrated) {
                this.router.navigateByUrl(
                  `/?email=${encodeURIComponent(resp.email)}`,
                  { replaceUrl: true }
                );
                return;
              }
              this.formState.set('login');
              setTimeout(() => this.passwordEl().nativeElement.focus(), 0);
            },
            error: () => this.alertFlash.addDanger(),
          });
        break;
      case 'login':
        if (this.credentialsForm().invalid()) return;
        this.formState.set('mfa');
        break;
      case 'mfa':
        if (this.mfaForm().invalid()) {
          this.alertFlash.addDanger(
            `Form invalid: ${this.mfaForm
              .code()
              .errors()
              .map((e) => e.message)
              .join(', ')}`
          );
          return;
        }
        this.alertFlash.addSuccess(
          `You submitted ${this.mfaForm.code().value()}.`
        );
        break;
    }
  }

  togglePassword() {
    this.showPassword.update((shown) => !shown);
  }

  goBack() {
    this.credentials.set({ email: '', password: '' });
    this.mfaFormModel.set({ code: '' });
    this.credentialsForm().reset();
    this.mfaForm().reset();
    this.formState.set('enterEmail');
    setTimeout(() => {
      this.emailEl().nativeElement.focus();
    }, 0);
  }

  resetPassword() {
    if (this.credentialsForm.email().invalid()) return;
    this.alertFlash.addSuccess('Please check your email.');
    this.formState.set('mfa');
  }
}
